/**
 * « Voir l'animation » d'une SKILLCARD (lot 4) : sous la carte sur sa page,
 * et dans la fenêtre « Cours » du player. Rien tant que la carte n'a pas
 * d'animation publiée. Le résumé (`get_card_animation`) suffit à l'annoncer ;
 * le document n'est chargé qu'au toucher.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Lock, Play, X } from 'lucide-react';
import { JRAF_PALETTE } from '@jeraisonne/animation';
import { cn, MathText } from '@jeraisonne/ui';
import type { ToolId } from '@/features/toolbox/catalog';
import { useCardAnimationRef } from '@/lib/animations/published';
import { PublishedAnimationView } from './published-animation';

export function CardAnimationBlock({
  cardId,
  onUnlock,
  onOpenTool,
  compact = false,
}: {
  cardId: string;
  /** « Débloquer » à la fin de la partie gratuite */
  onUnlock?: () => void;
  onOpenTool?: (toolId: ToolId) => void;
  /** fenêtre « Cours » : sans cadre ni lien vers la page */
  compact?: boolean;
}) {
  const ref = useCardAnimationRef(cardId);
  const [open, setOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const openRef = useRef<HTMLButtonElement>(null);
  const toggled = useRef(false);
  // ouvert : le lecteur entier à l'écran (il est plus haut que le bouton) ;
  // refermé : le focus revient au bouton qui l'a ouvert
  useEffect(() => {
    if (!toggled.current) return;
    if (open) sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    else openRef.current?.focus();
  }, [open]);
  const toggle = (next: boolean) => {
    toggled.current = true;
    setOpen(next);
  };
  const a = ref.data;
  if (!a) return null;

  if (!open) {
    return (
      <button
        ref={openRef}
        type="button"
        onClick={() => toggle(true)}
        data-testid="card-animation-open"
        className="flex min-h-16 w-full items-center gap-3 rounded-2xl p-3 text-left shadow-sm active:scale-[0.99]"
        style={{ backgroundColor: JRAF_PALETTE.night, color: JRAF_PALETTE.paper }}
      >
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: JRAF_PALETTE.gold, color: JRAF_PALETTE.night }}
        >
          <Play className="h-6 w-6" fill="currentColor" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="font-display block text-base font-bold">Voir l’animation</span>
          <span className="block truncate text-[13px] opacity-80">
            <MathText>{a.title}</MathText>
          </span>
          {a.has_interactive && (
            <span className="mt-0.5 flex items-center gap-1 text-xs opacity-70">
              <Lock className="h-3 w-3" aria-hidden /> partie « À toi » avec la SKILLCARD
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <section
      ref={sectionRef}
      aria-label="Animation de la carte"
      className={cn(!compact && 'border-ink-200 space-y-2 rounded-2xl border bg-white p-3')}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-display text-ink-900 min-w-0 truncate text-sm font-bold">
          <MathText>{a.title}</MathText>
        </p>
        <button
          type="button"
          onClick={() => toggle(false)}
          aria-label="Fermer l’animation"
          className="text-ink-500 hover:bg-ink-100 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>
      <PublishedAnimationView slug={a.slug} onUnlock={onUnlock} onOpenTool={onOpenTool} />
      {!compact && (
        <Link
          to="/animation/$slug"
          params={{ slug: a.slug }}
          className="text-primary-600 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold"
        >
          Page de l’animation (à partager) <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </Link>
      )}
    </section>
  );
}
